import { ItemsViewLayout } from '@app/components/items-view-layout-switch/ItemsViewLayoutSwitch';
import { createContext, PropsWithChildren, useContext } from 'react';

interface StatusViewContextValue {
  compact: boolean;
  /** Current list/grid layout; used for layout-specific UI (e.g. status badge on gridWide). */
  variant: ItemsViewLayout;
  /** Measured width of one grid item; 0 until first measure. */
  itemWidth: number;
}

const StatusViewContext = createContext<StatusViewContextValue | undefined>(undefined);

interface StatusViewProviderProps {
  compact: boolean;
  variant: ItemsViewLayout;
  /** Measured width of one grid item; 0 until first measure. */
  itemWidth: number;
}

/**
 * Provider component that supplies compact mode and layout variant to all child components.
 * Used to avoid prop drilling when components need to know if they're in grid (compact) or list view.
 */
export function StatusViewProvider({
  compact,
  variant,
  itemWidth,
  children,
}: PropsWithChildren<StatusViewProviderProps>) {
  return (
    <StatusViewContext.Provider value={{ compact, variant, itemWidth }}>
      {children}
    </StatusViewContext.Provider>
  );
}

/**
 * Hook to access the status view context.
 * Throws an error if used outside of StatusViewProvider.
 */
export function useStatusViewContext(): StatusViewContextValue {
  const context = useContext(StatusViewContext);
  if (context === undefined) {
    throw new Error('useStatusViewContext must be used within a StatusViewProvider');
  }
  return context;
}
